import { FeatureTourDefinition } from '../types/featureTour';
import store from '@/store';
import { RUNNIT_TYPE } from '@run-diffusion/shared';
import { SELECTED_PROMPT_VIEW } from '@/views/Runnits/constants';

const TOUR_MEDIA_BASE = 'https://cdn.rundiffusion.com/file/rd-web-assets/image-ai/feature-tours/single-tool';
const singleToolStep1 = `${TOUR_MEDIA_BASE}/single_tool_step_1.mp4`;
const singleToolStep2 = `${TOUR_MEDIA_BASE}/single_tool_step_2.mp4`;
const singleToolStep3 = `${TOUR_MEDIA_BASE}/single_tool_step_3.mp4`;
const singleToolStep4 = `${TOUR_MEDIA_BASE}/single_tool_step_4.mp4`;
const singleToolStep5 = `${TOUR_MEDIA_BASE}/single_tool_step_5.mp4`;
const singleToolStep6 = `${TOUR_MEDIA_BASE}/single_tool_step_6.mp4`;
const singleToolStep7 = `${TOUR_MEDIA_BASE}/single_tool_step_7.mp4`;

// Single Tool mode has two interchangeable form layouts: the modern
// prompt-driven form (PrimaryForm.vue) and the legacy settings drawer.
// This tour walks the prompt-driven UI specifically — every anchor below
// only exists when PrimaryForm is rendered. If the user is on a tool that
// doesn't support prompt-driven, or has flipped their preference to the
// drawer, the auto-launch gate below skips the tour entirely so we don't
// spotlight elements that aren't on screen.
const isPromptDrivenActive = (): boolean => {
	const selectedNodeId = store.state.runnitState?.selectedNodeId;
	const selectedNode = selectedNodeId ? store.state.runnitNodesMap?.[selectedNodeId] : null;
	const nodeDef = selectedNode?.nodeDef;
	const nodeDefSupports = !!(nodeDef && typeof nodeDef !== 'string' && nodeDef.usePromptDrivenForm);
	const userPrefersPromptDriven = store.state.runnitState?.selectedPromptView === SELECTED_PROMPT_VIEW.PROMPT_DRIVEN;
	return nodeDefSupports && userPrefersPromptDriven;
};

const isSingleToolBoard = (): boolean => {
	return store.state.runnitState?.runnit?.type === RUNNIT_TYPE.SINGLE_TOOL;
};

export const singleToolFirstOpen: FeatureTourDefinition = {
	id: 'single-tool-first-open',
	title: 'A quick tour of Single Tool mode',
	version: 1,
	eyebrowLabel: 'Single Tool 101',
	// Auto-launch the first time a user lands on a single-tool board running
	// the prompt-driven form. The drawer-mode UI surfaces different anchors
	// (and is the legacy fallback), so this tour intentionally skips it.
	autoLaunch: () => isSingleToolBoard() && isPromptDrivenActive(),
	steps: [
		{
			id: 'single-tool-overview',
			target: '[data-feature-tour="single-tool-canvas"]',
			title: 'Welcome to your tool',
			description: 'Single Tool mode keeps one tool front-and-center. Configure it, run it, and your generations show up right here on the same page.',
			mediaUrl: singleToolStep1,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Single Tool overview',
			placement: 'center',
		},
		{
			id: 'single-tool-prompt',
			target: '[data-feature-tour="single-tool-prompt"]',
			title: 'Start with a prompt',
			description: 'Describe what you want the tool to make. A clear prompt gives the model direction, style, and intent.',
			mediaUrl: singleToolStep2,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Prompt input',
			placement: 'top',
		},
		{
			id: 'single-tool-chips',
			target: '[data-feature-tour="single-tool-chips"]',
			title: 'Quick settings live here',
			description: 'Aspect ratio, model variant, and other one-tap options sit as chips along the bottom — tweak them inline without leaving the page.',
			mediaUrl: singleToolStep3,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Chip fields',
			placement: 'top',
		},
		{
			id: 'single-tool-image-input',
			target: '[data-feature-tour="single-tool-image-input"]',
			title: 'Bring your own image',
			description: 'For image-to-image and edit tools, drop a starting image here. The model uses it as the base for what it generates next.',
			mediaUrl: singleToolStep4,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Image input',
			placement: 'top',
		},
		{
			id: 'single-tool-run',
			target: '[data-feature-tour="single-tool-run"]',
			title: 'Run when you’re ready',
			description: 'Hit Run to generate. The token cost is shown next to the button, and you can keep iterating without leaving the page.',
			mediaUrl: singleToolStep5,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Run button',
			placement: 'top',
		},
		{
			id: 'single-tool-change-tool',
			target: '[data-feature-tour="single-tool-change-tool"]',
			title: 'Switch tools any time',
			description: 'Click the tool name at the bottom to swap to a different tool. Each tool keeps its own settings and history, so you can hop between them without losing your work.',
			mediaUrl: singleToolStep6,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Change tool',
			placement: 'top',
		},
		{
			id: 'single-tool-gallery',
			// The single-tool-gallery anchor wraps the entire results pane (full
			// page width × 800+ px tall), which makes the driver.js spotlight
			// meaningless and forces the card all the way to the top. Narrow the
			// target to the filter row inside the gallery so the spotlight lands
			// on something useful — fall back to the wrapper if the filter row
			// isn't mounted yet (e.g. transient empty-state).
			target: () => {
				const gallery = document.querySelector('[data-feature-tour="single-tool-gallery"]') as HTMLElement | null;
				if (!gallery) return null;
				return (gallery.querySelector('.filter-toggle-container') as HTMLElement | null) ?? gallery;
			},
			title: 'Your results show up below',
			description: 'Every generation lands here. Use the filter at the top — switch to This Tool to see only what you’ve made with this tool, or browse Featured for inspiration.',
			mediaUrl: singleToolStep7,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Results gallery',
			placement: 'bottom',
		},
	],
};
